import React, { useEffect, useMemo, useState } from 'react';
import { Auth } from 'aws-amplify';
import { useLocation } from 'react-router-dom';
import { CognitoUser, CognitoUserPool } from 'amazon-cognito-identity-js';
import AuthorizationService from '../../../../engage/service/AuthorizationService';
import MainContainer from '../../../common/component/MainContainer';
import FormContainer from '../../../common/component/FormContainer';
import Nav from '../../../common/component/Nav/Nav';
import { CustomButton } from '../../../../engage/components/Button/Button';
import { CustomInput } from '../../../../engage/components/Input/Input';
import styles from '../otp-page.module.scss';
import { useTranslation } from 'react-i18next';
import { ENTER_KEY_CODE } from '../../../../engage/constants/common';
import { sanitizeReturnUrl } from '../../../common/util/returnUrl';

const VerifyOTP = props => {
  const location = useLocation();
  const routeState = location.state;
  const userDetails = JSON.parse(localStorage.getItem('userDetails'));

  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [username, setUsername] = useState(null);
  const [response, setResponse] = useState(null);

  const [userCode, setUserCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const isValidCode = useMemo(() => userCode && userCode.length === 6, [userCode]);
  const hasError = error !== '';

  useEffect(() => {
    if (routeState !== undefined) {
      const routeStateObj = JSON.parse(routeState);
      setUser(routeStateObj.user);
      if (routeStateObj.hasOwnProperty('response')) {
        setResponse(routeStateObj.response);
      }
      if (routeStateObj.hasOwnProperty('username')) {
        setUsername(routeStateObj.username);
      }
    }
    if (userDetails !== null) {
      props.history.push({
        pathname: '/eg/dashboard',
      });
    }
    if (!routeState) {
      props.history.push({
        pathname: '/login',
      });
    }
  }, []);

  useEffect(() => {
    if (userCode.length > 6) {
      setError('6 Digits only');
    }
  }, [userCode]);

  const onCodeChange = e => {
    const value = e.target.value;
    if (value.length <= 6 && isNaN(value) === false) {
      setUserCode(value);
      setError('');
    }
  };

  const updateUserMFAAttribute = async () => {
    const user = await Auth.currentAuthenticatedUser();
    const updateRes = await Auth.updateUserAttributes(user, {
      'custom:enabled_mfa': '1',
    });
  };

  const onSubmitClick = async () => {
    console.log('stateUser', user);
    console.log('stateResponse', response);
    console.log('stateUsername', username);
    // return;
    setLoading(true);
    if (user !== null && username !== null) {
      // from loginPage
      const pool = new CognitoUserPool({
        UserPoolId: user.pool.userPoolId,
        ClientId: user.pool.clientId,
        endpoint: user.client.endpoint,
        Storage: window.localStorage,
        AdvancedSecurityDataCollectionFlag: user.advancedSecurityDataCollectionFlag,
      });
      const cognitoUser = new CognitoUser({
        Username: user.username,
        Pool: pool,
        Storage: window.localStorage,
      });
      cognitoUser.Session = user.Session;
      try {
        const { user, response } = await AuthorizationService.confirmLogin(cognitoUser, userCode, username);
        localStorage.setItem(
          'userDetails',
          JSON.stringify({
            username: user.username,
            userPoolId: user.pool.userPoolId,
            appClientId: user.pool.clientId,
            identityPoolId: response.IdentityPoolId,
            userPoolWebClientId: response.UserPoolClientAppId,
            email: user.signInUserSession.idToken.payload.email,
            name: user.signInUserSession.idToken.payload.name,
            companyId: response.companyId,
            companyName: response.companyName,
            region: response.Region,
            bucket: response.InputBucketName,
            ssoDomain: response.SSODomain,
          })
        );
      } catch (err) {
        setLoading(false);
        setError('The code is invalid. Please try again.');
        return;
      }
    } else {
      // from OTP/setup page
      try {
        const authcognitoUser = await AuthorizationService.getCognitoAuthUser();
        const verifyTOTP = await AuthorizationService.verifyTOTP(authcognitoUser, userCode);
        const setPreferdMFA = await AuthorizationService.setPrefredMFA(authcognitoUser, 'TOTP');
        const getPreferdMFA = await AuthorizationService.getPrefredMFA(authcognitoUser);
        localStorage.setItem(
          'userDetails',
          JSON.stringify({
            username: user.username,
            userPoolId: user.pool.userPoolId,
            appClientId: user.pool.clientId,
            identityPoolId: response.IdentityPoolId,
            userPoolWebClientId: response.UserPoolClientAppId,
            email: user.signInUserSession == null ? user.challengeParam.userAttributes.email : user.attributes.email,
            name: user.signInUserSession == null ? user.challengeParam.userAttributes.name : user.attributes.name,
            companyId: response.companyId,
            companyName: response.companyName,
            region: response.Region,
            bucket: response.InputBucketName,
            ssoDomain: response.SSODomain,
          })
        );
      } catch (err) {
        setLoading(false);
        setError('The code is invalid. Please try again.');
        return;
      }
    }
    updateUserMFAAttribute();
    setLoading(false);
    const returnUrl = new URLSearchParams(props.location.search).get('returnUrl');
    const safeReturnUrl = sanitizeReturnUrl(returnUrl);
    if (safeReturnUrl) {
      props.history.push(safeReturnUrl);
    } else {
      props.history.push('/eg/home');
    }
  };

  const handleEnterClick = e => {
    if (e.keyCode === ENTER_KEY_CODE) {
      onSubmitClick();
    }
  };

  useEffect(() => {
    isValidCode ? document.addEventListener('keydown', handleEnterClick) : document.removeEventListener('keydown', handleEnterClick);

    return () => document.removeEventListener('keydown', handleEnterClick);
  }, [isValidCode]);

  return (
    <>
      <Nav />
      <MainContainer className="content-padding play-bg">
        <div className="grid-container">
          <div className="prefix-25 grid-50 tablet-prefix-20 tablet-grid-60 mobile-grid-100">
            <FormContainer title="Multi Factor Authentication" className="text-center">
              <div className="flex-center">
                <div className="auth-card" style={{ minWidth: '560px', maxWidth: '640px' }}>
                  <p className={styles.messageText} style={{ marginBottom: '24px' }}>
                    {t('mfa.auth_message')}
                  </p>
                  <CustomInput
                    type="text"
                    name="code"
                    inputClassName="userCode-input"
                    value={userCode}
                    fieldName="Digit code"
                    placeholder={t('mfa.code_placeholder')}
                    invalid={hasError}
                    validateMessage={error}
                    onChange={onCodeChange}
                    autoFocus={true}
                  />
                  <CustomButton
                    variant="primary"
                    label={t('general.continue')}
                    onClick={onSubmitClick}
                    loading={loading}
                    disabled={hasError || !isValidCode}
                    loadingText={t('mfa.validating')}
                    className={styles.submitButton}
                  />
                </div>
              </div>
            </FormContainer>
          </div>
        </div>
      </MainContainer>
    </>
  );
};

export default VerifyOTP;
